import { normalizeApiErrorDetail } from '@/lib/normalizeApiError';

/**
 * Lấy API base URL (bao gồm /api)
 * Dùng cho axios/fetch requests
 */
export const getApiBaseUrl = (): string => {
  const raw = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
  if (raw) return raw.replace(/\/$/, '');

  // Default to production API
  return 'https://api-sys.isofh.vn/api';
};

/**
 * Lấy API origin (CHỈ origin, không có /api)
 * Dùng cho resolver ảnh/upload URLs
 */
export const getApiOrigin = (): string => {
  const raw = (import.meta.env.VITE_API_ORIGIN as string | undefined)?.trim();
  if (raw) return raw.replace(/\/$/, '');

  // Fallback: extract origin từ VITE_API_URL hoặc default
  const apiBase = getApiBaseUrl();
  // Nếu apiBase có /api ở cuối, bỏ đi
  if (apiBase.endsWith('/api')) {
    return apiBase.slice(0, -4); // Bỏ '/api'
  }
  // Nếu không có /api, giả định là origin
  return apiBase || 'https://api-sys.isofh.vn';
};

interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  ok: boolean;
  /** Present for blob downloads — used for Content-Disposition filename. */
  headers?: {
    contentType?: string | null;
    contentDisposition?: string | null;
  };
}

export interface ApiErrorPayload {
  kind: 'auth' | 'network' | 'server' | 'validation' | 'unknown';
  status: number;
  code?: string;
  message: string;
  error?: string;
}

/**
 * Get authentication headers for API requests
 * Always includes Authorization Bearer token if available
 * This ensures all API requests are authenticated
 */
const getAuthHeaders = (): HeadersInit => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  // Always attach token if available (prevents 401 errors)
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const handleResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  let data: unknown;
  
  try {
    data = await response.json();
  } catch (e) {
    // If response is not JSON, create a default error object
    data = {
      success: false,
      message: 'Phan hoi may chu khong hop le',
    };
  }
  
  const code = typeof data === 'object' && data !== null ? (data as { code?: string }).code : undefined;

  // 401: Unauthorized (data-only, no session side-effect)
  if (response.status === 401) {
    return {
      data: {
        ...data,
        kind: 'auth',
        code,
        message: data?.message || 'Phiên đăng nhập hết hạn, vui lòng đăng nhập lại',
      },
      status: response.status,
      ok: false,
    };
  }

  // 403: Forbidden
  if (response.status === 403) {
    const kind = code === 'ACCOUNT_LOCKED' ? 'auth' : 'validation';
    return {
      data: {
        ...data,
        kind,
        code,
      },
      status: response.status,
      ok: false,
    };
  }

  // 500: Internal Server Error - data-only, UI layer decides feedback surface
  if (response.status >= 500) {
    return {
      data: {
        ...data,
        kind: 'server',
        code,
      },
      status: response.status,
      ok: false,
    };
  }

  return {
    data,
    status: response.status,
    ok: response.ok,
  };
};

const api = {
  get: async <T = unknown>(path: string, options?: { responseType?: 'blob' | 'json'; signal?: AbortSignal; headers?: HeadersInit }): Promise<ApiResponse<T>> => {
    try {
      const headers = { ...getAuthHeaders(), ...options?.headers } as HeadersInit;
      const response = await fetch(`${getApiBaseUrl()}${path}`, {
        method: 'GET',
        headers,
        credentials: 'include', // CRITICAL: Include credentials for CORS (cookies, auth headers)
        signal: options?.signal, // Support AbortController
      });
      
      // Handle blob response (for file downloads)
      if (options?.responseType === 'blob') {
        // Check if request was aborted
        if (options?.signal?.aborted) {
          throw new DOMException('Yeu cau da bi huy.', 'AbortError');
        }
        
        if (!response.ok) {
          // Try to parse error message from JSON if available
          let errorData: unknown = { success: false, message: 'Khong the tai tep' };
          try {
            const text = await response.text();
            errorData = JSON.parse(text);
          } catch {
            // Ignore parse error, use default
          }
          return {
            data: errorData as T,
            status: response.status,
            ok: false,
          };
        }
        
        // Read blob (this may throw if aborted)
        let blob: Blob;
        try {
          blob = await response.blob();
        } catch (blobErr: unknown) {
          if (blobErr?.name === 'AbortError' || options?.signal?.aborted) {
            throw new DOMException('Yeu cau da bi huy.', 'AbortError');
          }
          throw blobErr;
        }
        
        return {
          data: blob as T,
          status: response.status,
          ok: true,
          headers: {
            contentType: response.headers.get('Content-Type'),
            contentDisposition: response.headers.get('Content-Disposition'),
          },
        };
      }
      
      return await handleResponse<T>(response);
    } catch (error: unknown) {
      // Check if aborted
      if (error?.name === 'AbortError' || options?.signal?.aborted) {
        throw new DOMException('Yeu cau da bi huy.', 'AbortError');
      }
      
      // For blob requests, re-throw error so component can handle it
      if (options?.responseType === 'blob') {
        throw error;
      }
      
      // Network error or other fetch failures (for non-blob requests)
      return {
        data: {
          success: false,
          kind: 'network',
          message: 'Không thể kết nối máy chủ. Vui lòng kiểm tra mạng.',
          error: error instanceof Error ? error.message : 'Loi khong xac dinh',
        },
        status: 0,
        ok: false,
      };
    }
  },

  post: async <T = unknown>(
    path: string,
    body?: unknown,
    options?: { headers?: HeadersInit; signal?: AbortSignal; timeout?: number; responseType?: 'blob' }
  ): Promise<ApiResponse<T>> => {
    const timeoutController = new AbortController();
    let timeoutId: NodeJS.Timeout | null = null;
    let combinedController: AbortController | null = null;
    let cleanupCombined: (() => void) | null = null;

    // Set up timeout if provided
    if (options?.timeout) {
      timeoutId = setTimeout(() => {
        timeoutController.abort();
      }, options.timeout);
    }

    // Combine user abort + timeout abort so both work together.
    const buildCombinedSignal = (): AbortSignal => {
      const userSignal = options?.signal;

      // If only one signal exists, use it directly.
      if (!userSignal) return timeoutController.signal;
      if (!options?.timeout) return userSignal;

      // Prefer native AbortSignal.any when available.
      const anyFn = (AbortSignal as unknown as { any?: (signals: AbortSignal[]) => AbortSignal }).any;
      if (typeof anyFn === 'function') {
        return anyFn([userSignal, timeoutController.signal]);
      }

      // Fallback: manual bridge controller.
      combinedController = new AbortController();
      const onAbort = () => {
        if (!combinedController!.signal.aborted) combinedController!.abort();
      };
      userSignal.addEventListener('abort', onAbort);
      timeoutController.signal.addEventListener('abort', onAbort);
      cleanupCombined = () => {
        userSignal.removeEventListener('abort', onAbort);
        timeoutController.signal.removeEventListener('abort', onAbort);
      };
      return combinedController.signal;
    };

    try {
      const headers = { ...getAuthHeaders(), ...options?.headers };
      const signal = buildCombinedSignal();
      const isFormData = body instanceof FormData;
      if (isFormData) {
        (headers as Record<string, string>)['Content-Type'] = undefined;
        delete (headers as Record<string, string>)['Content-Type'];
      }
      const response = await fetch(`${getApiBaseUrl()}${path}`, {
        method: 'POST',
        headers,
        body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
        credentials: 'include', // CRITICAL: Include credentials for CORS
        signal,
      });

      if (timeoutId) clearTimeout(timeoutId);
      if (cleanupCombined) cleanupCombined();

      if (options?.responseType === 'blob') {
        const blob = await response.blob();
        return {
          data: blob as T,
          status: response.status,
          ok: response.ok,
        };
      }
      return await handleResponse<T>(response);
    } catch (error: unknown) {
      if (timeoutId) clearTimeout(timeoutId);
      if (cleanupCombined) cleanupCombined();

      // User-cancelled abort: UI layer decides feedback surface
      if (options?.signal?.aborted) {
        return {
          data: {
            success: false,
            kind: 'network',
            message: 'Yeu cau da bi huy',
            error: 'Yeu cau da bi huy',
          },
          status: 0,
          ok: false,
        };
      }

      // Timeout abort (only) - data-only, UI layer decides feedback surface
      if (error?.name === 'AbortError' || timeoutController.signal.aborted || combinedController?.signal.aborted) {
        return {
          data: {
            success: false,
            kind: 'network',
            message: 'Hệ thống phản hồi chậm, vui lòng thử lại.',
            error: 'Het thoi gian cho phan hoi',
          },
          status: 0,
          ok: false,
        };
      }

      // Network error or other fetch failures
      return {
        data: {
          success: false,
          kind: 'network',
          message: 'Không thể kết nối máy chủ. Vui lòng kiểm tra mạng.',
          error: error instanceof Error ? error.message : 'Loi khong xac dinh',
        },
        status: 0,
        ok: false,
      };
    }
  },

  put: async <T = unknown>(path: string, body?: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(`${getApiBaseUrl()}${path}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'include', // CRITICAL: Include credentials for CORS
      });
      return await handleResponse<T>(response);
    } catch (error) {
      // Network error or other fetch failures
      return {
        data: {
          success: false,
          kind: 'network',
          message: 'Không thể kết nối máy chủ. Vui lòng kiểm tra mạng.',
          error: error instanceof Error ? error.message : 'Loi khong xac dinh',
        },
        status: 0,
        ok: false,
      };
    }
  },

  patch: async <T = unknown>(path: string, body?: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(`${getApiBaseUrl()}${path}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'include', // CRITICAL: Include credentials for CORS
      });
      return await handleResponse<T>(response);
    } catch (error) {
      // Network error or other fetch failures
      return {
        data: {
          success: false,
          kind: 'network',
          message: 'Không thể kết nối máy chủ. Vui lòng kiểm tra mạng.',
          error: error instanceof Error ? error.message : 'Loi khong xac dinh',
        },
        status: 0,
        ok: false,
      };
    }
  },

  delete: async <T = unknown>(path: string): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(`${getApiBaseUrl()}${path}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include', // CRITICAL: Include credentials for CORS
      });
      return await handleResponse<T>(response);
    } catch (error) {
      // Network error or other fetch failures
      return {
        data: {
          success: false,
          kind: 'network',
          message: 'Không thể kết nối máy chủ. Vui lòng kiểm tra mạng.',
          error: error instanceof Error ? error.message : 'Loi khong xac dinh',
        },
        status: 0,
        ok: false,
      };
    }
  },
};

/**
 * Fetch file as blob with authentication
 * Used for images/files that need Authorization header (Bearer token)
 * Returns object URL that can be used in <img src> or download
 */
export const fetchFileBlob = async (url: string): Promise<string> => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Can dang nhap de truy cap tep');
    }
    if (response.status === 403) {
      throw new Error('Bạn không có quyền truy cập file này');
    }
    if (response.status === 404) {
      throw new Error('File không tồn tại');
    }
    throw new Error(`Khong the tai tep (${response.status})`);
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export default api;

export const toApiErrorPayload = (errorOrResponse: unknown): ApiErrorPayload => {
  const normalized = normalizeApiErrorDetail(errorOrResponse);
  return {
    kind: normalized.kind,
    status: normalized.status ?? 0,
    code: normalized.code,
    message: normalized.message,
  };
};
